import type { Account } from "@repo/shared-types/account";
import type { Me } from "@repo/shared-types/approvals";
import { LINEAR_DIRECTORY_REFRESH_PARAM, LINEAR_DIRECTORY_REFRESH_VALUE } from "../linear-directory-refresh.ts";
import { recordRequestFailure } from "./request-failures.ts";

export class RequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`request failed: ${status}`);
    this.name = "RequestError";
  }
}

const responseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

export const req = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const startedAt = performance.now();
  let response: Response | undefined;
  try {
    response = await fetch(path, {
      credentials: "include",
      ...init,
      headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    });
    if (!response.ok) throw new RequestError(response.status, await responseBody(response));
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  } catch (error) {
    if (!(error instanceof Error && error.name === "AbortError") && !init?.signal?.aborted) {
      recordRequestFailure({
        path,
        method: init?.method,
        status: response?.status ?? null,
        route: response?.headers.get("x-portal-route"),
        correlationId: response?.headers.get("x-request-id"),
        durationMs: performance.now() - startedAt,
        ...(error instanceof RequestError ? { body: error.body } : { errorCode: response ? "invalid_response" : "network_error" }),
      });
    }
    throw error;
  }
};

export const me = (): Promise<Me> => req<Me>("/me");

export const account = (): Promise<Account> => req<Account>("/api/account");

export type LinearDirectoryFetchOptions = {
  fresh?: boolean;
};

// Client half of the contract in shared/linear-directory-refresh.ts: a Linear-backed selector asks for
// uncached data only when the user pressed Refresh, so no caller hand-writes the parameter into a path.
export const withRefresh = (path: string, options?: LinearDirectoryFetchOptions): string => {
  if (!options?.fresh) return path;
  return `${path}${path.includes("?") ? "&" : "?"}${LINEAR_DIRECTORY_REFRESH_PARAM}=${LINEAR_DIRECTORY_REFRESH_VALUE}`;
};
